import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Upload, UserPlus, Users as UsersIcon, SearchX,
  ChevronRight, CheckCircle2, Shapes, CircleDot, ArrowDownNarrowWide,
  Pencil, X, Plus, Download, Archive, Trash2, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import {
  Avatar, Badge, Button, Card, EmptyState, FilterMenu, KV, MemberStatusPill,
  MembershipStatusPill, SearchInput, SectionTag,
} from '../components/ui';
import {
  useMember, useMembers, useSections, useArchiveMember, useReactivateMember, useDeleteMember, useUpdateMember,
  useRemoveMembership, useUpdateMembership, useAudit,
} from '../api/queries';
import { useMe } from '../api/queries';
import { downloadFile, openAuthedHtml } from '../api/client';
import { AuditIcon } from '../components/AuditIcon';
import {
  alter, fmtDate, fmtDateTime, initials, MITGLIEDSART_LABEL,
  AUDIT_ACTION_META, memberStatus,
} from '../lib/format';
import type { CurrentUser, MemberListItem } from '../api/types';
import { useToast } from '../lib/toast';
import { EditMemberModal } from './EditMemberModal';
import { AddMembershipModal } from './AddMembershipModal';
import { ImportMembersModal } from './ImportMembersModal';

export function MembersScreen({ user, onCreate }: { user: CurrentUser; onCreate: () => void }) {
  const nav = useNavigate();
  const { id: routeId } = useParams();
  const [query, setQuery] = useState('');
  const [filterSec, setFilterSec] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>('aktiv');
  const [sortBy, setSortBy] = useState<string>('name');
  const [importOpen, setImportOpen] = useState(false);

  const { data: sections = [] } = useSections();
  const { data: members = [], isLoading } = useMembers({
    q: query || undefined,
    status: filterStatus ?? undefined,
    section: filterSec ?? undefined,
    sort: sortBy,
  });

  const hasSearch = query.length > 0;
  const selectedId = routeId ?? null;

  return (
    <div className="page-fade">
      <PageHeader
        title="Mitglieder"
        subtitle={`${members.length} angezeigt${filterStatus === 'aktiv' ? ' · nur aktive' : ''}`}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Download size={16} />}
              onClick={async () => { try { await downloadFile('/members/export.csv', 'mitglieder.csv'); } catch { /* */ } }}
            >
              Exportieren
            </Button>
            <Button variant="secondary" icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>
              Importieren
            </Button>
            <Button variant="primary" icon={<UserPlus size={16} />} onClick={onCreate}>Neues Mitglied</Button>
          </>
        }
      />

      <Card>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Name, Nr., Ort oder Funktion…" />
          <FilterMenu
            label="Sektion"
            icon={<Shapes size={12} />}
            value={filterSec}
            options={sections.map((s) => ({ value: s.id, label: s.name, dot: s.color }))}
            onChange={setFilterSec}
          />
          <FilterMenu
            label="Status"
            icon={<CircleDot size={12} />}
            value={filterStatus}
            options={[
              { value: 'aktiv', label: 'aktiv' },
              { value: 'ausgetreten', label: 'ausgetreten' },
              { value: 'verstorben', label: 'verstorben' },
            ]}
            onChange={setFilterStatus}
          />
          <div className="spacer" />
          <FilterMenu
            label="Sortierung"
            icon={<ArrowDownNarrowWide size={12} />}
            value={sortBy}
            allowClear={false}
            options={[
              { value: 'name', label: 'Nachname' },
              { value: 'nummer', label: 'Mitgliedsnummer' },
              { value: 'eintritt', label: 'Eintrittsdatum' },
            ]}
            onChange={(v) => setSortBy(v ?? 'name')}
          />
        </div>

        {isLoading ? (
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 44 }} />)}
          </div>
        ) : members.length === 0 ? (
          hasSearch ? (
            <EmptyState icon={<SearchX size={20} />} title={`Keine Treffer für „${query}"`} subtitle="Versuche andere Stichworte oder entferne Filter." />
          ) : (
            <EmptyState
              icon={<UsersIcon size={20} />}
              title="Noch keine Mitglieder"
              subtitle="Lege das erste Mitglied an."
              action={<Button variant="primary" icon={<UserPlus size={16} />} onClick={onCreate}>Neues Mitglied</Button>}
            />
          )
        ) : (
          <>
            <MembersTable
              members={members}
              sections={sections}
              selectedId={selectedId}
              onSelect={(id) => nav(`/mitglieder/${id}`)}
            />
            <MembersCards
              members={members}
              sections={sections}
              onSelect={(id) => nav(`/mitglieder/${id}`)}
            />
          </>
        )}
      </Card>

      <MemberInspector
        memberId={selectedId}
        user={user}
        onClose={() => nav('/mitglieder')}
      />

      <ImportMembersModal open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

function MembersTable({
  members, sections, selectedId, onSelect,
}: {
  members: MemberListItem[];
  sections: { id: string; name: string; color: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const secMap = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);
  return (
    <div className="members-table table-wrap scroll-area" style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 56 }}>Nr.</th>
            <th>Name</th>
            <th>Sektion(en)</th>
            <th>Funktion</th>
            <th>Ort</th>
            <th>Status</th>
            <th style={{ width: 32 }}></th>
          </tr>
        </thead>
        <tbody className="stagger">
          {members.map((m, i) => {
            const ms = memberStatus(m);
            return (
              <tr
                key={m.id}
                className={selectedId === m.id ? 'selected' : ''}
                onClick={() => onSelect(m.id)}
                style={{ cursor: 'pointer', animationDelay: `${Math.min(i, 16) * 22}ms` }}
              >
                <td><span className="t-mono text-tertiary" style={{ fontSize: 12.5 }}>{String(m.mitgliedsnummer).padStart(3, '0')}</span></td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Avatar name={initials(m.vorname, m.nachname)} size="sm" />
                    <div>
                      <div style={{ fontWeight: 500 }}>{m.nachname}, {m.vorname}</div>
                      {ms !== 'aktiv' && (
                        <div style={{ marginTop: 2 }}><MemberStatusPill status={ms} /></div>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {m.sections.slice(0, 2).map((sId) => {
                      const sec = secMap.get(sId);
                      return sec ? <SectionTag key={sId} color={sec.color} name={sec.name} small /> : null;
                    })}
                    {m.sections.length > 2 && (
                      <span className="text-tertiary" style={{ fontSize: 11.5, alignSelf: 'center' }}>
                        +{m.sections.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td>
                  <span className="secondary-line">
                    {(() => {
                      const roles = Array.from(new Set(
                        (m.sectionStatuses ?? [])
                          .map((s) => s.funktion)
                          .filter((f): f is string => !!f && f !== 'Mitglied'),
                      ));
                      if (roles.length === 0) return <span className="text-tertiary">Mitglied</span>;
                      return <span style={{ fontWeight: 500 }}>{roles.slice(0, 2).join(', ')}{roles.length > 2 ? ` +${roles.length - 2}` : ''}</span>;
                    })()}
                  </span>
                </td>
                <td className="secondary-line">{m.ort}</td>
                <td>
                  <Badge tone={ms === 'aktiv' ? 'success' : 'neutral'} dot={ms === 'aktiv'}>{MITGLIEDSART_LABEL[m.mitgliedsart] ?? '–'}</Badge>
                </td>
                <td><ChevronRight size={14} className="text-quaternary" /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MembersCards({
  members, sections, onSelect,
}: {
  members: MemberListItem[];
  sections: { id: string; name: string; color: string }[];
  onSelect: (id: string) => void;
}) {
  const secMap = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);
  return (
    <div className="stagger">
      {members.map((m, i) => (
        <div
          key={m.id}
          className="member-card"
          onClick={() => onSelect(m.id)}
          style={{ animationDelay: `${Math.min(i, 14) * 24}ms`, cursor: 'pointer' }}
        >
          <Avatar name={initials(m.vorname, m.nachname)} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{m.nachname}, {m.vorname}</div>
            <div style={{ display: 'flex', gap: 5, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {m.sections.slice(0, 2).map((sId) => {
                const sec = secMap.get(sId);
                return sec ? <SectionTag key={sId} color={sec.color} name={sec.name} small /> : null;
              })}
              <span className="text-tertiary" style={{ fontSize: 12.5 }}>· Nr. {m.mitgliedsnummer}</span>
            </div>
          </div>
          <ChevronRight size={16} className="text-quaternary" />
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------
// Inspector (right slide-in)
// ----------------------------------------------------------------
function MemberInspector({
  memberId, user, onClose,
}: { memberId: string | null; user: CurrentUser; onClose: () => void }) {
  const open = !!memberId;
  const { data: m, isLoading: memberLoading } = useMember(memberId);
  const { data: sections = [] } = useSections();
  const me = useMe();
  const [tab, setTab] = useState<'stamm' | 'verein' | 'notiz' | 'audit'>('stamm');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notiz, setNotiz] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState<'stamm' | 'verein'>('stamm');
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [confirmRemoveSection, setConfirmRemoveSection] = useState<string | null>(null);

  const toast = useToast();
  const update = useUpdateMember(memberId ?? '');
  const archive = useArchiveMember();
  const reactivate = useReactivateMember();
  const del = useDeleteMember();
  const removeMembership = useRemoveMembership(memberId ?? '');

  const isAdmin = user.rolle === 'admin';
  const canEdit = user.rolle !== 'read_only';

  useEffect(() => {
    setTab('stamm');
    setConfirmDelete(false);
    setConfirmRemoveSection(null);
    setNotiz(m?.veraenderungen ?? '');
  }, [memberId, m?.veraenderungen]);

  const secMap = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);

  const openEdit = (which: 'stamm' | 'verein' = 'stamm') => {
    setEditTab(which);
    setEditOpen(true);
  };

  const allowedSectionIds: string[] | null =
    me.data && (me.data.user.rolle === 'admin' || me.data.user.rolle === 'read_only')
      ? null
      : me.data?.sectionIds ?? null;

  return (
    <>
      <div className={`inspector-overlay ${open ? 'show' : ''}`} onClick={onClose} />
      <div className={`inspector ${open ? 'show' : ''}`}>
        {open && !m && memberLoading && (
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div className="skeleton" style={{ width: 56, height: 56, borderRadius: 999 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div className="skeleton" style={{ height: 18, width: '60%' }} />
                <div className="skeleton" style={{ height: 12, width: '40%' }} />
              </div>
            </div>
            <div className="skeleton" style={{ height: 90, borderRadius: 8 }} />
            <div className="skeleton" style={{ height: 90, borderRadius: 8 }} />
            <div className="skeleton" style={{ height: 90, borderRadius: 8 }} />
          </div>
        )}
        {m && (
          <>
            <div className="inspector-header">
              <div className="inspector-top-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={initials(m.vorname, m.nachname)} size="lg" />
                  <div>
                    <div className="t-title-3">{m.vorname} {m.nachname}</div>
                    <div className="t-caption text-tertiary" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span className="t-mono">Nr. {String(m.mitgliedsnummer).padStart(3, '0')}</span> ·{' '}
                      <MemberStatusPill status={memberStatus(m)} />
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 2 }}>
                  {canEdit && (
                    <Button variant="ghost" size="sm" icon={<Pencil size={16} />} title="Bearbeiten" aria-label="Mitglied bearbeiten" onClick={() => openEdit(tab === 'verein' ? 'verein' : 'stamm')} />
                  )}
                  <Button variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} title="Schließen" aria-label="Inspector schließen" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                {m.sections.map((sId) => {
                  const s = secMap.get(sId);
                  return s ? <SectionTag key={sId} color={s.color} name={s.name} /> : null;
                })}
                <span className="section-tag" style={{ background: 'var(--accent-soft)', color: 'var(--accent)', border: 'none' }}>
                  {MITGLIEDSART_LABEL[m.mitgliedsart]}
                </span>
              </div>
              <div className="inspector-tabs">
                {([['stamm', 'Stammdaten'], ['verein', 'Verein'], ['notiz', 'Notizen'], ...(isAdmin ? [['audit', 'Verlauf']] : [])] as const).map(
                  ([id, label]) => (
                    <button key={id} className={`inspector-tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id as typeof tab)} type="button">{label}</button>
                  ),
                )}
              </div>
            </div>

            <div className="inspector-body scroll-area">
              {tab === 'stamm' && (
                <>
                  <div className="section-block" style={{ marginTop: 0 }}>
                    <div className="section-block-title">Persönliches</div>
                    <div className="kv-list">
                      <KV k="Vorname">{m.vorname}</KV>
                      <KV k="Nachname">{m.nachname}</KV>
                      <KV k="Geburtsdatum">
                        {fmtDate(m.geburtsdatum)}{' '}
                        {m.geburtsdatum && <span className="text-tertiary">({alter(m.geburtsdatum)} J.)</span>}
                      </KV>
                      <KV k="Geschlecht">{m.geschlecht === 'm' ? 'männlich' : m.geschlecht === 'w' ? 'weiblich' : m.geschlecht === 'd' ? 'divers' : '–'}</KV>
                    </div>
                  </div>
                  <div className="section-block">
                    <div className="section-block-title">Kontakt</div>
                    <div className="kv-list">
                      <KV k="Adresse">{m.strasse}<br />{m.plz} {m.ort}</KV>
                      <KV k="Telefon"><span className="t-mono" style={{ fontSize: 13 }}>{m.telefon ?? '–'}</span></KV>
                      <KV k="E-Mail"><span className="t-mono" style={{ fontSize: 13 }}>{m.email ?? '–'}</span></KV>
                    </div>
                  </div>
                </>
              )}

              {tab === 'verein' && (
                <>
                  <div className="section-block" style={{ marginTop: 0 }}>
                    <div className="section-block-title">Vereinsdaten</div>
                    <div className="kv-list">
                      <KV k="Mitgliedsnummer"><span className="t-mono">{String(m.mitgliedsnummer).padStart(3, '0')}</span></KV>
                      <KV k="Funktion">{m.funktion}</KV>
                      <KV k="Mitgliedsart">{MITGLIEDSART_LABEL[m.mitgliedsart]}</KV>
                      <KV k="Eingetreten">{fmtDate(m.eingetretenAm)}</KV>
                      {m.ausgetretenAm && <KV k="Ausgetreten">{fmtDate(m.ausgetretenAm)}</KV>}
                    </div>
                  </div>
                  <div className="section-block">
                    <div className="section-block-title">Sektionen</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {m.memberships.map((mb) => {
                        const sec = secMap.get(mb.section);
                        const canManageThis = !allowedSectionIds || allowedSectionIds.includes(mb.section);
                        const isConfirming = confirmRemoveSection === mb.section;
                        return (
                          <div key={mb.section} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg-muted)', borderRadius: 8 }}>
                            <span className="sec-dot" style={{ background: sec?.color, width: 10, height: 10 }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, fontSize: 14 }}>{sec?.name ?? mb.section}</div>
                              <div className="text-tertiary" style={{ fontSize: 12 }}>
                                {mb.funktion && mb.funktion !== 'Mitglied' ? <><span style={{ color: 'var(--accent)', fontWeight: 500 }}>{mb.funktion}</span> · </> : null}
                                seit {fmtDate(mb.eingetreten_am)}{mb.notizen ? ` · ${mb.notizen}` : ''}
                              </div>
                            </div>
                            {canEdit && canManageThis && (
                              <MembershipFunctionEditor
                                memberId={m.id}
                                section={mb.section}
                                value={mb.funktion ?? 'Mitglied'}
                              />
                            )}
                            {canEdit && canManageThis ? (
                              <MembershipStatusEditor
                                memberId={m.id}
                                section={mb.section}
                                value={mb.status}
                              />
                            ) : (
                              <MembershipStatusPill status={mb.status} />
                            )}
                            {canEdit && canManageThis && (
                              isConfirming ? (
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <Button variant="ghost" size="sm" onClick={() => setConfirmRemoveSection(null)}>Abbruch</Button>
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => removeMembership.mutate(mb.section, {
                                      onSuccess: () => { toast.push(`${sec?.name ?? 'Sektion'} entfernt`); setConfirmRemoveSection(null); },
                                      onError: () => { toast.push('Konnte nicht entfernen', 'warn'); setConfirmRemoveSection(null); },
                                    })}
                                  >Entfernen?</Button>
                                </div>
                              ) : (
                                <Button variant="ghost" size="sm" icon={<X size={14} />} title="Aus Sektion entfernen"
                                  aria-label={`${sec?.name ?? 'Sektion'} aus Mitgliedschaft entfernen`}
                                  onClick={() => setConfirmRemoveSection(mb.section)} />
                              )
                            )}
                          </div>
                        );
                      })}
                      {m.memberships.length === 0 && (
                        <div className="muted-card">In keiner Sektion gemeldet.</div>
                      )}
                      {canEdit && (
                        <Button variant="secondary" icon={<Plus size={14} />} size="sm" onClick={() => setAddSectionOpen(true)}>
                          Sektion hinzufügen
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}

              {tab === 'notiz' && (
                <div className="section-block" style={{ marginTop: 0 }}>
                  <div className="section-block-title">Bemerkungen / Veränderungen</div>
                  <textarea
                    className="input"
                    value={notiz}
                    onChange={(e) => setNotiz(e.target.value)}
                    placeholder={'Freitext-Notizen zu diesem Mitglied (wie in der bisherigen Excel-Spalte „Veränderungen") …'}
                    style={{ minHeight: 140 }}
                    disabled={!canEdit}
                  />
                  {canEdit && (
                    <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() =>
                          update.mutate(
                            { veraenderungen: notiz },
                            { onSuccess: () => toast.push('Notiz gespeichert') },
                          )
                        }
                      >
                        Notiz speichern
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {tab === 'audit' && (
                <MemberAuditTab memberId={m.id} />
              )}
            </div>

            <div style={{ padding: 14, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                icon={<Download size={14} />}
                size="sm"
                onClick={async () => {
                  try { await downloadFile(`/members/${m.id}/export`, `mitglied_${m.mitgliedsnummer}.json`); }
                  catch { toast.push('Export fehlgeschlagen', 'warn'); }
                }}
                title="Vollständiger JSON-Datenexport"
              >
                JSON-Export
              </Button>
              <Button
                variant="secondary"
                icon={<Download size={14} />}
                size="sm"
                onClick={async () => {
                  try { await openAuthedHtml(`/members/${m.id}/print`); }
                  catch { toast.push('Öffnen fehlgeschlagen', 'warn'); }
                }}
                title="DSGVO-Auskunft als druckbare PDF"
              >
                PDF-Auskunft
              </Button>
              {/* DSGVO-gelöschte Datensätze tragen ebenfalls status 'ausgetreten',
                  dürfen aber NICHT zurückgeholt werden — der Name ist unwiderruflich
                  weg und die Löschung wurde ausdrücklich verlangt. */}
              {m.anonymisiert && (
                <span className="text-tertiary" style={{ fontSize: 12.5, alignSelf: 'center' }}>
                  DSGVO-gelöscht am {fmtDate(m.anonymisiertAm)} — nicht wiederherstellbar
                </span>
              )}
              {/* Archivieren und Zurücknehmen — ohne das Gegenstück wäre ein
                  versehentlicher Austritt über die Oberfläche nicht korrigierbar. */}
              {canEdit && !m.anonymisiert && (m.status === 'ausgetreten' ? (
                <Button
                  variant="ghost"
                  icon={<RotateCcw size={14} />}
                  size="sm"
                  disabled={reactivate.isPending}
                  onClick={() => reactivate.mutate(m.id, {
                    onSuccess: () => toast.push('Austritt zurückgenommen — wieder aktiv'),
                    onError: () => toast.push('Konnte nicht zurückgenommen werden', 'warn'),
                  })}
                >
                  Austritt zurücknehmen
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  icon={<Archive size={14} />}
                  size="sm"
                  disabled={archive.isPending}
                  onClick={() => archive.mutate(m.id, { onSuccess: () => { toast.push('Mitglied archiviert'); onClose(); } })}
                >
                  Archivieren
                </Button>
              ))}
              {isAdmin && !m.anonymisiert && (confirmDelete
                ? <Button variant="danger" icon={<AlertTriangle size={14} />} size="sm" onClick={() => del.mutate(m.id, { onSuccess: () => { toast.push('Mitglied gelöscht'); setConfirmDelete(false); onClose(); } })}>Wirklich löschen?</Button>
                : <Button variant="ghost" icon={<Trash2 size={14} />} size="sm" onClick={() => setConfirmDelete(true)} className="text-danger">Löschen</Button>
              )}
            </div>

            <EditMemberModal
              open={editOpen}
              onClose={() => setEditOpen(false)}
              member={m}
              user={user}
              initialTab={editTab}
            />
            <AddMembershipModal
              open={addSectionOpen}
              onClose={() => setAddSectionOpen(false)}
              member={m}
              allowedSectionIds={allowedSectionIds}
            />
          </>
        )}
      </div>
    </>
  );
}

// ----------------------------------------------------------------
// Audit-Log eines Mitglieds
// ----------------------------------------------------------------
function MemberAuditTab({ memberId }: { memberId: string }) {
  const { data: entries = [], isLoading } = useAudit({ datensatz_id: memberId });

  return (
    <div className="section-block" style={{ marginTop: 0 }}>
      <div className="section-block-title">Letzte Änderungen an diesem Mitglied</div>
      {isLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 36 }} />)}
        </div>
      )}
      {!isLoading && entries.length === 0 && (
        <div className="muted-card" style={{ fontSize: 13 }}>
          Noch keine protokollierten Änderungen.
        </div>
      )}
      {!isLoading && entries.map((l) => {
        const meta = AUDIT_ACTION_META[l.aktion] ?? { tone: 'neutral' as const, icon: 'circle', label: l.aktion };
        return (
          <div key={l.id} className="activity-row" style={{ paddingLeft: 0, paddingRight: 0 }}>
            <div className={`ic ${meta.tone}`}><AuditIcon name={meta.icon} /></div>
            <div className="text">
              <span className="who">{l.u_vorname} {l.u_nachname}</span> · {l.feld}
              <div className="when">{fmtDateTime(l.timestamp)} · {l.ip_adresse ?? '–'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------
// Inline-Editor für Mitgliedschaftsstatus pro Sektion
// ----------------------------------------------------------------
function MembershipStatusEditor({
  memberId, section, value,
}: { memberId: string; section: string; value: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const update = useUpdateMembership(memberId);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const options: { value: 'mitglied' | 'aktiv' | 'funktionaer' | 'ruhend'; label: string }[] = [
    { value: 'mitglied', label: 'Mitglied' },
    { value: 'aktiv', label: 'aktiv' },
    { value: 'funktionaer', label: 'Funktionär' },
    { value: 'ruhend', label: 'ruhend' },
  ];

  const set = (status: 'mitglied' | 'aktiv' | 'funktionaer' | 'ruhend') => {
    setOpen(false);
    if (status === value) return;
    update.mutate(
      { section, patch: { status } },
      {
        onSuccess: () => toast.push('Status geändert'),
        onError: () => toast.push('Konnte nicht ändern', 'warn'),
      },
    );
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
        aria-label="Mitgliedschaftsstatus ändern"
      >
        <MembershipStatusPill status={value} />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: 4, minWidth: 160, zIndex: 30,
            animation: 'slideUp 180ms cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set(opt.value)}
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 6, textAlign: 'left',
                fontSize: 13, color: 'var(--text-primary)',
                display: 'flex', alignItems: 'center', gap: 8,
                background: value === opt.value ? 'var(--accent-soft)' : 'transparent',
                border: 'none', cursor: 'pointer',
              }}
            >
              <MembershipStatusPill status={opt.value} />
              <span style={{ flex: 1 }} />
              {value === opt.value && <CheckCircle2 size={13} style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Inline-Editor für Funktion pro Sektion
// ----------------------------------------------------------------
const FUNKTIONEN = [
  'Mitglied', 'Obmann', 'Obmann Stv.', 'Sektionsleiter', 'Sektionsleiter Stv.',
  'Kassier', 'Kassier Stv.', 'Schriftführer', 'Schriftführer Stv.',
  'Beirat', 'Trainer', 'Spieler',
];

function MembershipFunctionEditor({
  memberId, section, value,
}: { memberId: string; section: string; value: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const update = useUpdateMembership(memberId);
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const set = (funktion: string) => {
    setOpen(false);
    if (funktion === value) return;
    update.mutate(
      { section, patch: { funktion } },
      {
        onSuccess: () => toast.push('Funktion geändert'),
        onError: () => toast.push('Konnte nicht ändern', 'warn'),
      },
    );
  };

  const isDefault = value === 'Mitglied' || !value;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Funktion ändern"
        style={{
          background: isDefault ? 'transparent' : 'var(--accent-soft)',
          color: isDefault ? 'var(--text-tertiary)' : 'var(--accent)',
          border: `1px solid ${isDefault ? 'var(--border-default)' : 'transparent'}`,
          padding: '3px 8px', borderRadius: 6, cursor: 'pointer',
          fontSize: 12, fontWeight: 500,
        }}
        aria-label="Funktion ändern"
      >
        {isDefault ? '+ Funktion' : value}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
            borderRadius: 10, boxShadow: 'var(--shadow-md)', padding: 4, minWidth: 200, zIndex: 30,
            maxHeight: 300, overflowY: 'auto',
            animation: 'slideUp 180ms cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          {FUNKTIONEN.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => set(f)}
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 6, textAlign: 'left',
                fontSize: 13, color: 'var(--text-primary)',
                display: 'flex', alignItems: 'center', gap: 8,
                background: value === f ? 'var(--accent-soft)' : 'transparent',
                border: 'none', cursor: 'pointer',
              }}
            >
              <span style={{ flex: 1 }}>{f}</span>
              {value === f && <CheckCircle2 size={13} style={{ color: 'var(--accent)' }} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
